import { encode as mpEncode, decode as mpDecode } from '@msgpack/msgpack'
import type { ClientFrame, ServerFrame } from './frames.js'

export type AnyFrame = ClientFrame | ServerFrame

const CLIENT_FRAME_TYPES: ReadonlySet<ClientFrame['type']> = new Set([
  'auth', 'open', 'send', 'sync', 'history', 'read', 'typing', 'react', 'edit', 'delete', 'invoke', 'pubkey', 'ping',
  'uploadPrekeys', 'fetchPrekey', 'assign', 'tag', 'note', 'agent_status', 'subscribe_inbox', 'unsubscribe_inbox',
])

/** True if a decoded frame is one a client is allowed to send. The server uses
 *  this to reject server-only frame types before dispatch, so a malicious or
 *  buggy client can't reach an unexpected handler path. */
export function isClientFrame(frame: AnyFrame): frame is ClientFrame {
  return CLIENT_FRAME_TYPES.has(frame.type as ClientFrame['type'])
}

/** Encode a frame to a binary msgpack payload for the wire. */
export function encodeFrame(frame: AnyFrame): Uint8Array {
  return mpEncode(frame)
}

/** Decode a binary payload into a frame. Returns null on any malformed input or
 *  anything lacking a string `type`, so a bad frame can never crash the handler
 *  — the boundary validates `type` before trusting the rest. */
export function decodeFrame(bytes: Uint8Array): AnyFrame | null {
  let value: unknown
  try {
    value = mpDecode(bytes)
  } catch {
    return null
  }
  if (typeof value !== 'object' || value === null) return null
  if (typeof (value as { type?: unknown }).type !== 'string') return null
  return value as AnyFrame
}
